/**
 * Watch a popup sign-in by the server row, not by the popup (#1179). A provider whose sign-in page sets
 * Cross-Origin-Opener-Policy (Coda) severs `window.opener` — and `popup.closed` — so the callback page cannot
 * message this window. The backend callback finishes the exchange itself (for the browser we bound), and this
 * polls the row until it is connected, records a failure reason, or the bound runs out.
 */
export interface SignInServer {
  id: string;
  connected: boolean;
  /** The sign-in is still open on the server. A row already connected (a re-sign-in) is done only once this clears. */
  signingIn?: boolean;
  /** Why the last sign-in did not finish (the callback recorded it). */
  signInError?: string;
}

export type SignInOutcome = { kind: 'connected' } | { kind: 'failed'; reason: string } | { kind: 'timeout' } | { kind: 'cancelled' };

export function watchSignIn(opts: { serverId: string; fetchRows: () => Promise<SignInServer[]>; intervalMs?: number; maxMs?: number; signal?: AbortSignal }): Promise<SignInOutcome> {
  const { serverId, fetchRows, intervalMs = 2000, maxMs = 3 * 60_000, signal } = opts;
  const started = Date.now();
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (o: SignInOutcome) => {
      if (timer) clearTimeout(timer);
      resolve(o);
    };
    const tick = async () => {
      if (signal?.aborted) return finish({ kind: 'cancelled' });
      if (Date.now() - started >= maxMs) return finish({ kind: 'timeout' });
      try {
        const row = (await fetchRows()).find((s) => s.id === serverId);
        if (signal?.aborted) return finish({ kind: 'cancelled' });
        if (row?.connected && !row.signingIn) return finish({ kind: 'connected' });
        if (row?.signInError) return finish({ kind: 'failed', reason: row.signInError });
      } catch {
        // a blip — the next tick retries; the bound still applies
      }
      timer = setTimeout(tick, intervalMs);
    };
    signal?.addEventListener('abort', () => finish({ kind: 'cancelled' }), { once: true });
    timer = setTimeout(tick, intervalMs);
  });
}
